/**
 * Short version of the Risikoanalyse step-form schema.
 *
 * Mirrors the 4-step structure defined in `for-ai/kundenmodus.json`
 * (Unternehmen & Struktur, Kennzahlen & Menschen, Tätigkeit/Standorte/
 * Vermögen, Risiken & Abschluss).
 *
 * **Ein Key pro Frage** (PO-Entscheidung 18.08.2026): Stellt eine Frage hier
 * dasselbe wie in der Langform, trägt sie auch denselben Antwort-Key — den der
 * Langform. Nur so sieht der Berater eine Kurzform-Einreichung in der
 * Auswertung, die ausschließlich das Langform-Schema rendert.
 *
 * Ausgenommen sind die neun Paare in `AKTIVE_ANTWORT_KEY_ALIASSE`
 * (`answerKeys.ts`), deren `type` oder Optionsliste auseinandergeht — ein
 * gemeinsamer Key würde dort Angaben verfälschen. Die Keys aus der Zeit vor
 * der Umbenennung stehen in `HISTORISCHE_ANTWORT_KEYS` und werden beim Lesen
 * aufgelöst; wer hier einen Key ändert, gehört dort ergänzt.
 */
import { FIRMA_ROLLE_OPTIONS, FIRMA_VERBINDUNG_OPTIONS, RECHTSFORM_OPTIONS, } from "./formOptions.js";
const yn = (question, key, followUp, hint) => ({
    question,
    hint,
    field: { key, type: "yesno" },
    ...(followUp
        ? { showFollowUpWhen: { key, equals: "ja" }, followUpFields: followUp }
        : {}),
});
const q = (question, field, hint) => ({
    question,
    hint,
    field,
});
const qMulti = (question, fields, opts) => ({
    question,
    hint: opts?.hint,
    layout: opts?.layout,
    fields,
});
const REGISTER_OPTIONS = [
    { value: "HRB", label: "HRB — Handelsregister B" },
    { value: "HRA", label: "HRA — Handelsregister A" },
    { value: "PR", label: "PR — Partnerschaftsregister" },
    { value: "GnR", label: "GnR — Genossenschaftsregister" },
    { value: "VR", label: "VR — Vereinsregister" },
    { value: "Keines", label: "Keines" },
];
const EIGENTUM_OPTIONS = [
    { value: "Eigentum", label: "Eigentum" },
    { value: "Miete / Pacht / Leasing", label: "Miete / Pacht / Leasing" },
];
const GF_FUNCTIONS = [
    { value: "Geschäftsführer", label: "Geschäftsführer" },
    { value: "Vorstand", label: "Vorstand" },
    { value: "Kaufmännischer Leiter", label: "Kaufmännischer Leiter" },
];
const GS_FUNCTIONS = [
    { value: "Gesellschafter", label: "Gesellschafter" },
    {
        value: "Geschäftsführender Gesellschafter",
        label: "Geschäftsführender Gesellschafter",
    },
];
const VERANT_FUNCTIONS = [
    "Steuern",
    "Recht",
    "Bank",
    "Notar",
    "IT",
    "Datenschutz",
    "Buchhaltung",
    "Sicherheit",
    "Brandschutz",
    "Compliance",
    "QM",
].map((v) => ({ value: v, label: v }));
export const stepFormSchemaKurz = {
    sections: [
        // ───────────────────────── 1. Unternehmen & Struktur ─────────────────────────
        {
            key: "unternehmen_struktur",
            title: "Unternehmen & Struktur",
            color: "orange",
            subSteps: [
                {
                    key: "stammdaten",
                    title: "Stammdaten",
                    description: "Bitte prüfen und gegebenenfalls korrigieren Sie die hinterlegten Stammdaten.",
                    questions: [
                        qMulti("Sind die hinterlegten Stammdaten korrekt?", [
                            { key: "name", label: "Name / Firma", type: "text" },
                            { key: "telefon", label: "Telefon", type: "text", format: "phone" },
                            { key: "strasse", label: "Straße & Hausnummer", type: "text" },
                            { key: "fax", label: "Fax", type: "text", format: "phone" },
                            { key: "plz", label: "PLZ", type: "text" },
                            { key: "mail", label: "E-Mail", type: "text", format: "email" },
                            { key: "stadt", label: "Ort", type: "text" },
                            { key: "webseite", label: "Webseite", type: "text", format: "url" },
                            { key: "land", label: "Land", type: "text" },
                        ], {
                            layout: "two",
                            hint: "Bitte alle Felder kontrollieren und ggf. anpassen.",
                        }),
                        yn("Sind die Stammdaten korrekt?", "stammdaten_korrekt"),
                        q("Wer ist Ihr Gesprächspartner für diese Analyse?", {
                            key: "gespraechspartner",
                            label: "Gesprächspartner (Name, Funktion)",
                            type: "text",
                        }),
                        q("An welchem Datum führen Sie die Analyse durch?", {
                            key: "gespraechsdatum",
                            label: "Gesprächsdatum",
                            type: "date",
                        }),
                    ],
                },
                {
                    key: "gruendung",
                    title: "Gründung & allgemeine Informationen",
                    questions: [
                        q("Wann wurde Ihr Unternehmen gegründet?", {
                            key: "gruendungsjahr",
                            label: "Gründungsdatum",
                            type: "date",
                        }),
                        q("Wer war der Gründer des Unternehmens?", {
                            key: "gruender",
                            label: "Name des Gründers",
                            type: "text",
                        }),
                        yn("Gibt es einen Rechtsvorgänger des heutigen Unternehmens?", "rechtsvorgaenger_ja", [
                            {
                                key: "rechtsvorgaenger_details",
                                label: "Welcher Rechtsvorgänger, wann und warum verändert?",
                                type: "textarea",
                            },
                        ]),
                    ],
                },
                {
                    key: "rechtsform",
                    title: "Rechtsform & Registrierung",
                    questions: [
                        q("Welche Rechtsform hat Ihr Unternehmen?", {
                            key: "rechtsform",
                            label: "Rechtsform",
                            type: "select",
                            options: RECHTSFORM_OPTIONS,
                        }),
                        {
                            question: "Welche Funktion hat Ihr Unternehmen?",
                            hint: "Mehrfachauswahl möglich.",
                            field: {
                                key: "rolle_unternehmen",
                                label: "Rolle des Unternehmens",
                                type: "checkboxes",
                                options: FIRMA_ROLLE_OPTIONS,
                            },
                        },
                        qMulti("Wie lautet die Handelsregisternummer und in welches Register ist das Unternehmen eingetragen?", [
                            {
                                key: "hr_nummer",
                                label: "Handelsregisternummer",
                                type: "text", format: "hrnummer",
                            },
                            {
                                key: "handelsregister_art",
                                label: "Register",
                                type: "select",
                                options: REGISTER_OPTIONS,
                            },
                        ]),
                        qMulti("Wie lautet Ihre Steuernummer und Umsatzsteuernummer und welches Finanzamt ist für Ihr Unternehmen zuständig?", [
                            { key: "steuernummer", label: "Steuernummer", type: "text" },
                            {
                                key: "ust_id",
                                label: "Umsatzsteuer-Identifikationsnummer",
                                type: "text", format: "ustIdNr",
                            },
                            {
                                key: "finanzamt",
                                label: "Zuständiges Finanzamt",
                                type: "text",
                            },
                        ]),
                        yn("Ist das Unternehmen vorsteuerabzugsberechtigt?", "vorsteuerabzug"),
                        yn("Ist das Geschäftsjahr gleich dem Kalenderjahr?", "geschaeftsjahr_kalender"),
                        {
                            question: "Abweichendes Geschäftsjahr: Von Monat/Tag bis Monat/Tag",
                            showWhen: { key: "geschaeftsjahr_kalender", equals: "nein" },
                            field: {
                                key: "geschaeftsjahr_abweichung",
                                label: "z. B. 01.07. – 30.06.",
                                type: "text",
                            },
                        },
                        yn("Sind Sie bilanzierungspflichtig?", "bilanzpflicht_ja", [
                            {
                                key: "pruefungspflicht",
                                label: "Auch Prüfungspflicht (§ 316 HGB)? (ja/nein)",
                                type: "text",
                            },
                        ]),
                        {
                            question: "Wirtschaftsprüfer / Buchprüfer (Name, Anschrift, Ansprechpartner)",
                            showWhenAll: [{ key: "bilanzpflicht_ja", equals: "ja" }],
                            field: {
                                key: "wirtschaftspruefer",
                                label: "Angaben",
                                type: "textarea",
                            },
                        },
                    ],
                },
                {
                    key: "verbund",
                    title: "Unternehmensverbund u. Beteiligungen",
                    description: "Erfassen Sie hier alle Unternehmen, die zum Verbund gehören oder an denen eine Beteiligung besteht. Die übrigen Abschnitte beziehen sich weiterhin auf Ihr Hauptunternehmen.",
                    questions: [
                        yn("Gibt es weitere zum Unternehmensverbund gehörende Unternehmen?", "verbund_ja"),
                        {
                            question: "Unternehmen im Verbund",
                            hint: "Bitte je Unternehmen Firmierung, Anschrift, Rolle, Verbindung, Beteiligungsquote und Tätigkeit angeben.",
                            showWhen: { key: "verbund_ja", equals: "ja" },
                            field: {
                                key: "verbund_firmen",
                                type: "firmaEntries",
                                options: FIRMA_VERBINDUNG_OPTIONS,
                                functionOptions: FIRMA_ROLLE_OPTIONS,
                                withTaetigkeitFuer: true,
                            },
                        },
                        yn("Ist das Unternehmen an weiteren Unternehmen außerhalb des Unternehmensverbundes beteiligt?", "beteiligungen_ja"),
                        {
                            question: "Beteiligungen außerhalb des Unternehmensverbunds",
                            hint: "Bitte je Unternehmen Firmierung, Anschrift, Rolle, Verbindung, Beteiligungsquote und Tätigkeit angeben.",
                            showWhen: { key: "beteiligungen_ja", equals: "ja" },
                            field: {
                                key: "beteiligungen_firmen",
                                type: "firmaEntries",
                                options: FIRMA_VERBINDUNG_OPTIONS,
                                functionOptions: FIRMA_ROLLE_OPTIONS,
                            },
                        },
                    ],
                },
                {
                    key: "personen",
                    title: "Gesellschafter & Geschäftsführer",
                    questions: [
                        {
                            question: "Geschäftsführer",
                            field: {
                                key: "geschaeftsfuehrer",
                                type: "personEntries",
                                description: "Bitte alle aktiven Geschäftsführer eintragen.",
                                functionOptions: GF_FUNCTIONS,
                            },
                        },
                        {
                            question: "Gesellschafter",
                            field: {
                                key: "gesellschafter",
                                type: "personEntries",
                                description: "Auch Firmen können Gesellschafter sein. Bitte die Anteile in % angeben.",
                                withAnteile: true,
                                functionOptions: GS_FUNCTIONS,
                            },
                        },
                        yn("Haben die Gesellschafter / Beteiligten weitere Unternehmensbeteiligungen?", "gesellschafter_beteiligungen_ja", [
                            {
                                key: "gesellschafter_beteiligungen_details",
                                label: "An welchen Unternehmen, mit welchen Quoten und was machen diese Unternehmen?",
                                type: "textarea",
                            },
                        ]),
                        yn("Sind Veränderungen zum Unternehmen, zu den Gesellschafterstrukturen oder Beteiligungen angedacht?", "veraenderungen_ja", [
                            {
                                key: "veraenderungen_details",
                                label: "Welche Veränderungen sind geplant?",
                                type: "textarea",
                            },
                        ]),
                    ],
                },
                {
                    key: "verantwortlichkeiten",
                    title: "Verantwortlichkeiten und Zuständigkeiten fürs Unternehmen",
                    description: "Wer ist im Unternehmen für welche Bereiche zuständig? Extern angelegte Dienstleister werden bei der Auswertung als Firmen-Kontakt übernommen.",
                    questions: [
                        {
                            question: "Wer ist wofür im Unternehmen zuständig?",
                            hint: "Hierbei geht es darum, wer für bestimmte Bereiche (z.B. Recht, Steuern, IT, Datenschutz) im Unternehmen zuständig ist.",
                            field: {
                                key: "verantwortlichkeiten",
                                type: "verantEntries",
                                functionOptions: VERANT_FUNCTIONS,
                            },
                        },
                    ],
                },
            ],
        },
        // ───────────────────────── 2. Kennzahlen & Menschen ─────────────────────────
        {
            key: "kennzahlen_menschen",
            title: "Kennzahlen & Menschen",
            color: "green",
            subSteps: [
                {
                    key: "umsatz_finanzen",
                    title: "Umsatz & Finanzkennzahlen",
                    questions: [
                        q("Umsatz vorletztes Jahr (€)", {
                            key: "umsatz_v",
                            label: "Vorletztes Jahr",
                            type: "text", format: "currencyEUR",
                        }),
                        q("Umsatz letztes Jahr (€)", {
                            key: "umsatz_l",
                            label: "Letztes Jahr",
                            type: "text", format: "currencyEUR",
                        }),
                        q("Umsatz laufendes Jahr — Schätzung (€)", {
                            key: "umsatz_a",
                            label: "Laufendes Jahr",
                            type: "text", format: "currencyEUR",
                        }),
                        q("Wareneinsatz letztes Jahr (€)", {
                            key: "wareneinsatz_l",
                            label: "Wareneinsatz",
                            type: "text", format: "currencyEUR",
                        }),
                        q("Rohertrag letztes Jahr (€)", {
                            key: "rohertrag_l",
                            label: "Rohertrag",
                            type: "text", format: "currencyEUR",
                        }),
                    ],
                },
                {
                    key: "menschen",
                    title: "Menschen im Unternehmen",
                    questions: [
                        q("Wie viele Menschen arbeiten insgesamt für Ihr Unternehmen?", {
                            key: "mitarbeiter_gesamt",
                            label: "Mitarbeiter gesamt",
                            type: "number", format: "integer",
                        }),
                        q("Wie viele arbeiten zur gleichen Zeit?", {
                            key: "mitarbeiter_gleichzeitig",
                            label: "Gleichzeitig anwesend",
                            type: "number", format: "integer",
                        }),
                        q("Wie hoch ist die Bruttojahreslohn-/Gehaltssumme nach BG-Meldung?", {
                            key: "bg_gesamt",
                            label: "Lohn- & Gehaltssumme (€)",
                            type: "text", format: "currencyEUR",
                        }),
                        yn("Arbeiten Sie mit Leiharbeitern?", "leiharbeiter_ja", [
                            {
                                // Bestandsabweichung, NICHT durch „ein Key pro Frage" entstanden:
                                // Dieses Feld teilt sich den Key seit jeher mit der Langform,
                                // fasst dort aber „Mit wie vielen?" (format `integer`) und
                                // „Regelmäßig?" zusammen. Ein Rückzug auf einen eigenen Key
                                // würde Bestandsantworten verwaisen lassen — die Auflösung ist
                                // eine PO-Frage (siehe Kopfkommentar in `answerKeys.ts`).
                                key: "leiharbeiter_anzahl",
                                label: "Wie viele und regelmäßig?",
                                type: "text",
                            },
                        ]),
                        yn("Beauftragen Sie Subunternehmer?", "subunternehmer_ja", [
                            {
                                // Bewusst NICHT `subunternehmer_anteil`: Dieses eine Feld fasst
                                // zwei Langform-Fragen zusammen („Wie viele?" + „Umsatzanteil").
                                // Siehe Kopfkommentar in `answerKeys.ts`.
                                key: "subunternehmer_details",
                                label: "Wie viele und welcher Umsatzanteil?",
                                type: "text",
                            },
                        ]),
                        yn("Beschäftigen Sie Freelancer?", "freelancer_ja", [
                            {
                                // Bewusst NICHT `freelancer_anzahl`: fasst „Wie viele?" und
                                // „Aufwand" der Langform in einem Freitext zusammen.
                                key: "freelancer_details",
                                label: "Wie viele und regelmäßig?",
                                type: "text",
                            },
                        ]),
                        yn("Gibt es spezielle Versorgungen / Absicherungen für die Geschäftsführer und/oder Gesellschafter?", "gf_versorgung_ja", [
                            {
                                key: "gf_versorgung_details",
                                label: "Was und in welcher Höhe?",
                                type: "textarea",
                            },
                        ]),
                    ],
                },
            ],
        },
        // ───────────────────────── 3. Tätigkeit, Standorte & Vermögen ─────────────────────────
        {
            key: "taetigkeit_standorte_vermoegen",
            title: "Tätigkeit, Standorte & Vermögen",
            color: "pillarOrange",
            subSteps: [
                {
                    key: "taetigkeit",
                    title: "Unternehmenstätigkeit",
                    description: "Was Ihr Unternehmen macht — bitte in eigenen Worten.",
                    questions: [
                        q("Was macht Ihr Unternehmen genau? (Tätigkeit / Betriebscharakter)", {
                            key: "taetigkeit",
                            label: "Beschreibung",
                            placeholder: "Was ist Ihr Kerngeschäft? Was produzieren, vertreiben oder leisten Sie?",
                            type: "textarea",
                        }),
                        q("Gibt es darüber hinaus noch etwas?", {
                            key: "taetigkeit_weiteres",
                            label: "Ergänzungen",
                            type: "textarea",
                        }),
                    ],
                },
                {
                    key: "standorte",
                    title: "Standorte & Betriebsstätten",
                    description: "Wo befinden sich Ihre Standorte?",
                    questions: [
                        qMulti("1. Standort", [
                            {
                                key: "st1_strasse",
                                label: "Straße & Hausnummer",
                                type: "text",
                            },
                            { key: "standort_plz", label: "PLZ", type: "text" },
                            { key: "standort_ort", label: "Ort", type: "text" },
                        ], { layout: "two" }),
                        q("Handelt es sich um Eigentum oder ist die Immobilie angemietet, gepachtet oder geleast?", {
                            key: "st1_eigentumsverhaeltnis",
                            label: "Nutzungsart",
                            type: "select",
                            options: EIGENTUM_OPTIONS,
                        }),
                        {
                            question: "Wer ist Eigentümer?",
                            showWhen: {
                                key: "st1_eigentumsverhaeltnis",
                                equals: "Miete / Pacht / Leasing",
                            },
                            field: {
                                key: "st1_eigentuemer",
                                label: "Eigentümer",
                                type: "text",
                            },
                        },
                        qMulti("Fläche Standort 1", [
                            {
                                key: "st1_flaeche_gesamt",
                                label: "Fläche insgesamt (m²)",
                                type: "number", format: "area",
                            },
                            {
                                key: "st1_flaeche_genutzt",
                                label: "Davon selbst genutzt (m²)",
                                type: "number", format: "area",
                            },
                        ]),
                        yn("Erfolgt am Standort 1 eine Untervermietung?", "untervermietung_ja"),
                        yn("Ist Standort 1 auch Firmensitz?", "firmensitz_ja"),
                        yn("Ist Standort 1 auch Postanschrift?", "postanschrift_ja"),
                        qMulti("2. Standort (optional)", [
                            {
                                key: "st2_strasse",
                                label: "Straße & Hausnummer",
                                type: "text",
                            },
                            { key: "st2_plz", label: "PLZ", type: "text" },
                            { key: "st2_ort", label: "Ort", type: "text" },
                        ], { layout: "two" }),
                        q("Weitere Standorte?", {
                            key: "weitere_standorte",
                            label: "Bitte je Standort eine Zeile: Adresse, Eigentum/Miete, Fläche",
                            type: "textarea",
                        }),
                    ],
                },
                {
                    key: "vermoegen",
                    title: "Vermögenswerte & Fahrzeuge",
                    description: "Bitte Neuwerte angeben, wo möglich.",
                    questions: [
                        yn("Kaufmännische Betriebseinrichtung (Büromöbel, IT, etc.)?", "kaufm_betriebseinrichtung", [
                            {
                                key: "kaufm_betriebseinrichtung_details",
                                label: "Was, wo und welcher Neuwert in €?",
                                type: "textarea",
                            },
                        ]),
                        yn("Technische Betriebseinrichtung (Maschinen, Anlagen, Elektronik)?", "techn_betriebseinrichtung", [
                            {
                                key: "techn_betriebseinrichtung_details",
                                label: "Was, wo und welcher Neuwert in €?",
                                type: "textarea",
                            },
                        ]),
                        yn("Waren / Vorräte?", "waren_vorraete", [
                            {
                                key: "waren_vorraete_details",
                                label: "Was, wo und welcher Wert in €?",
                                type: "textarea",
                            },
                        ]),
                        yn("Grundstücke (im Eigentum des Unternehmens)?", "grundstuecke", [
                            {
                                key: "grundstuecke_details",
                                label: "Was, wo und welcher Wert in €?",
                                type: "textarea",
                            },
                        ]),
                        yn("Haben Sie eigene Kfz?", "kfz_eigene_ja", [
                            {
                                key: "eigene_kfz_details",
                                label: "Was und wie viele?",
                                type: "textarea",
                            },
                        ]),
                        yn("Sind fremde Kfz vorhanden?", "kfz_fremde_ja", [
                            {
                                key: "fremde_kfz_details",
                                label: "Was und wie viele?",
                                type: "textarea",
                            },
                        ]),
                        yn("Sonstige Vermögenswerte (Photovoltaik, Ladestationen, Wertsachen, etc.)?", "sonstige_vermoegenswerte", [
                            {
                                key: "sonstige_vermoegenswerte_details",
                                label: "Was, wo und welcher Neuwert in €?",
                                type: "textarea",
                            },
                        ]),
                    ],
                },
                {
                    key: "transport",
                    title: "Transport & Logistik",
                    questions: [
                        yn("Versenden Sie Waren?", "versand_ja", [
                            {
                                key: "versand_wie",
                                label: "Wie (eigene/fremde Fahrzeuge, Spedition, Paketservice, etc.)?",
                                type: "textarea",
                            },
                        ]),
                        q("Wie erhalten Sie nötige Betriebsmittel? (Bezüge)", {
                            key: "bezug_betriebsmittel",
                            label: "Beschreibung",
                            type: "textarea",
                        }),
                    ],
                },
            ],
        },
        // ───────────────────────── 4. Risiken & Abschluss ─────────────────────────
        {
            key: "risiken_abschluss",
            title: "Risiken & Abschluss",
            color: "purple",
            subSteps: [
                {
                    key: "risikobewertung",
                    title: "Risikobewertung",
                    description: "Welche Risiken sehen Sie für Ihr Unternehmen?",
                    questions: [
                        yn("Sehen Sie Haftungsrisiken für Ihr Unternehmen?", "haftungsrisiko", [
                            {
                                key: "haftungsrisiko_details",
                                label: "Welche Haftungsrisiken?",
                                type: "textarea",
                            },
                        ]),
                        yn("Sehen Sie Ausfall-, Finanz- und Liquiditätsrisiken?", "finanz_liquiditaetsrisiko", [
                            {
                                key: "finanz_liquiditaetsrisiko_details",
                                label: "Welche?",
                                type: "textarea",
                            },
                        ]),
                        yn("Sehen Sie Risiken aufgrund von Rechtsstreitigkeiten?", "rechtsstreitigkeitsrisiko", [
                            {
                                key: "rechtsstreitigkeitsrisiko_details",
                                label: "Welche?",
                                type: "textarea",
                            },
                        ]),
                        yn("Sehen Sie für Ihr Unternehmen Risiken aufgrund von Cyber?", "cyberangriff_ja", [
                            {
                                key: "cyberrisiko_details",
                                label: "Welche?",
                                type: "textarea",
                            },
                        ]),
                        yn("Sind Sie Schadenersatzrisiken aus Umweltgefährdung ausgesetzt? Gibt es Umweltrisiken oder Anlagen wie Öltank, Notstromaggregat, Öl-/Benzin-/Fettabscheider, Maschineninhalte oder brennbare Gase mit einem Gesamtgewicht ab 3 Tonnen?", "umwelt_grundrisiko_ja", [
                            {
                                key: "umwelt_grundrisiko_details",
                                label: "Welche?",
                                type: "textarea",
                            },
                        ]),
                    ],
                },
                {
                    key: "erwartungen",
                    title: "Erwartungen & Feedback",
                    questions: [
                        q("Wie sieht Ihre Erwartungshaltung als möglicher Kunde gegenüber Ihrem Berater aus? Auf was sollte dieser besonders achten?", {
                            key: "erwartung_berater",
                            label: "Erwartungshaltung",
                            type: "textarea",
                        }),
                        q("Gibt es gewünschte Besonderheiten bei den Vertragsgestaltungen / Risiken / Schäden / Selbstbeteiligungen?", {
                            key: "wuensche_vertrag",
                            label: "Besonderheiten",
                            type: "textarea",
                        }),
                        q("Wie zufrieden waren Sie bisher mit Ihrem Makler / Vermittler / Berater und Hauptversicherer?", {
                            key: "zufriedenheit_vor",
                            label: "Zufriedenheit",
                            type: "textarea",
                        }),
                        q("Was möchten Sie uns gerne noch mit auf den Weg geben?", {
                            key: "freitext_kunde",
                            label: "Freitext",
                            type: "textarea",
                        }),
                    ],
                },
                {
                    key: "unterlagen",
                    title: "Unterlagen-Upload",
                    description: "Bitte stellen Sie uns nötige Unterlagen zur Verfügung (Organigramm, Bilanz, AGB, Brandschutzbericht, etc.). Sie können diese im Anschluss an Ihren Berater senden.",
                    questions: [
                        q("Welche Unterlagen können Sie uns zur Verfügung stellen?", {
                            key: "unterlagen_hinweis",
                            label: "Bitte auflisten — z. B. Organigramm, Bilanz, AGB, Brandschutzbericht",
                            type: "textarea",
                        }),
                    ],
                },
                {
                    key: "antworten_pruefen",
                    title: "Daten prüfen & übermitteln",
                    description: 'Bitte prüfen Sie Ihre Antworten. Mit „Daten übermitteln" senden Sie die Risikoanalyse an Ihren Berater.',
                    customKind: "summary",
                    questions: [],
                },
            ],
        },
    ],
};
